import { getIdToken } from '@/lib/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL;
if (!API_BASE_URL) {
  throw new Error('VITE_API_URL environment variable is not defined');
}

const ENDPOINTS = {
  products: `${API_BASE_URL}/products`,
  feedback: `${API_BASE_URL}/feedback`,
  customForms: `${API_BASE_URL}/custom-forms`,
  integration: `${API_BASE_URL}/integration`,
  invites: `${API_BASE_URL}/invites`,
  members: `${API_BASE_URL}/members`,
};

const getAuthHeaders = async () => {
  const token = await getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

export interface Product {
  id: string;
  name: string;
  description: string;
  deletedAt?: string;
}

export interface Feedback {
  id: string;
  productId: string;
  submittedAt: string;
  content: string;
  summary?: string;
  sentiment?: string;
  tags?: string[];
}

// Products API
export const getProducts = async (): Promise<Product[]> => {
  const headers = await getAuthHeaders();
  const response = await fetch(ENDPOINTS.products, { headers });
  if (!response.ok) {
    throw new Error('Failed to fetch products');
  }
  return response.json();
};

export const createProduct = async (data: Omit<Product, 'id'>): Promise<Product> => {
  const headers = await getAuthHeaders();
  const response = await fetch(ENDPOINTS.products, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to create product');
  }
  return response.json();
};

export const updateProduct = async (id: string, data: Partial<Product>): Promise<Product> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ENDPOINTS.products}/${id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to update product');
  }
  return response.json();
};

export const deleteProduct = async (id: string): Promise<{ id: string; deletedAt: string }> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ENDPOINTS.products}/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    throw new Error('Failed to delete product');
  }
  return response.json();
};

// Feedback API
export const getProductFeedback = async (productId: string): Promise<Feedback[]> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ENDPOINTS.products}/${productId}/feedback`, { headers });
  if (!response.ok) {
    throw new Error('Failed to fetch product feedback');
  }
  return response.json();
};

export const getFeedbackById = async (id: string): Promise<Feedback | null> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ENDPOINTS.feedback}/${id}`, { headers });
  if (!response.ok) {
    throw new Error('Failed to fetch feedback');
  }
  return response.json();
};

// New feedback query API with filters, search, and pagination
export interface FeedbackQueryParams {
  product?: string;
  sentiment?: string;
  tags?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface FeedbackListResponse {
  results: (Feedback & { productName: string })[];
  total: number;
  page: number;
  pageSize: number;
}

export const getFeedbackList = async (params: FeedbackQueryParams = {}): Promise<FeedbackListResponse> => {
  const headers = await getAuthHeaders();
  const url = new URL(ENDPOINTS.feedback);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.append(key, String(value));
    }
  });
  const response = await fetch(url.toString(), { headers });
  if (!response.ok) {
    throw new Error('Failed to fetch feedback list');
  }
  return response.json();
};

// Custom Forms API
export interface CustomForm {
  id: string;
  name: string;
  productId: string;
  product: {
    id: string;
    name: string;
  };
  status: string;
  questions: any[];
}

export const getCustomForms = async (): Promise<CustomForm[]> => {
  const headers = await getAuthHeaders();
  const response = await fetch(ENDPOINTS.customForms, { headers });
  if (!response.ok) {
    throw new Error('Failed to fetch custom forms');
  }
  return response.json();
};

// Integration API
export interface GenerateTokenRequest {
  productId: string;
  customFormId?: string;
  expirationHours?: number;
}

export interface GeneratedTokenResponse {
  token: string;
  fullUrl: string;
  expiresAt: string | null;
  productName: string;
  customFormName?: string;
}

export const generateIntegrationToken = async (request: GenerateTokenRequest): Promise<GeneratedTokenResponse> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ENDPOINTS.integration}/generate-token`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    throw new Error('Failed to generate integration token');
  }
  return response.json();
};

// Onboarding API
export interface OnboardingStatus {
  hasCompletedOnboarding: boolean;
  productCount: number;
  shouldShowWelcome: boolean;
}

export const getOnboardingStatus = async (): Promise<OnboardingStatus> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/onboarding/status`, { headers });
  if (!response.ok) {
    throw new Error('Failed to fetch onboarding status');
  }
  return response.json();
};

export const completeOnboarding = async (): Promise<void> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/onboarding/complete`, {
    method: 'POST',
    headers,
  });
  if (!response.ok) {
    throw new Error('Failed to complete onboarding');
  }
};

export interface GenerateSampleDataResponse {
  success: boolean;
  message: string;
  data: {
    products: Array<{ id: string; name: string }>;
    feedbackCount: number;
  };
}

export const generateSampleData = async (): Promise<GenerateSampleDataResponse> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/onboarding/generate-sample-data`, {
    method: 'POST',
    headers,
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate sample data');
  }
  return response.json();
};

// ---------- Team invites + member management ----------

export type Role = 'ADMIN' | 'MEMBER' | 'VIEWER';

export interface Member {
  id: string;
  fullName: string;
  email: string | null;
  role: Role;
  createdAt: string;
}

export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';

export interface Invitation {
  id: string;
  companyId: string;
  email: string;
  role: Role;
  status: InvitationStatus;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  acceptedAt: string | null;
  invitedBy: { id: string; fullName: string; email: string | null } | null;
  acceptedBy: { id: string; fullName: string; email: string | null } | null;
}

// Custom error that preserves the server's structured error code so the UI
// can map individual cases to user-friendly messages (e.g. 409
// LAST_ADMIN_CANNOT_BE_DEMOTED → "The last admin can't be demoted").
export class ApiError extends Error {
  status: number;
  code: string | null;
  constructor(status: number, code: string | null, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

const parseError = async (response: Response): Promise<ApiError> => {
  let code: string | null = null;
  let message = `Request failed with status ${response.status}`;
  try {
    const body = await response.json();
    if (typeof body?.error === 'string') {
      code = body.error;
      message = body.message ?? body.error;
    }
  } catch {
    // Ignore parse failure; fall back to the default message.
  }
  return new ApiError(response.status, code, message);
};

// Members

export const getMembers = async (): Promise<Member[]> => {
  const headers = await getAuthHeaders();
  const response = await fetch(ENDPOINTS.members, { headers });
  if (!response.ok) throw await parseError(response);
  const data = await response.json();
  return data.members;
};

export const updateMemberRole = async (id: string, role: Role): Promise<Member> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ENDPOINTS.members}/${id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ role }),
  });
  if (!response.ok) throw await parseError(response);
  const data = await response.json();
  return data.member;
};

export const removeMember = async (id: string): Promise<void> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ENDPOINTS.members}/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) throw await parseError(response);
};

// Invitations (authenticated)

export interface EmailDeliveryStatus {
  // Did we call out to the email provider at all?
  attempted: boolean;
  // Did the provider accept the message?
  delivered: boolean;
  // Did we deliberately skip delivery (dev mode / no API key)?
  logOnly: boolean;
}

export interface CreateInvitationResponse {
  invitation: Invitation;
  acceptUrl: string;
  emailDelivery: EmailDeliveryStatus;
}

export const getInvitations = async (): Promise<Invitation[]> => {
  const headers = await getAuthHeaders();
  const response = await fetch(ENDPOINTS.invites, { headers });
  if (!response.ok) throw await parseError(response);
  const data = await response.json();
  return data.invitations;
};

export const createInvitation = async (email: string, role: Role): Promise<CreateInvitationResponse> => {
  const headers = await getAuthHeaders();
  const response = await fetch(ENDPOINTS.invites, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, role }),
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
};

export const resendInvitation = async (id: string): Promise<CreateInvitationResponse> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ENDPOINTS.invites}/${id}/resend`, {
    method: 'POST',
    headers,
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
};

export const revokeInvitation = async (id: string): Promise<Invitation> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ENDPOINTS.invites}/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) throw await parseError(response);
  const data = await response.json();
  return data.invitation;
};

// Invitation acceptance (public — no auth header)

export interface InvitationPreview {
  email: string;
  role: Role;
  companyName: string;
  invitedByName: string | null;
  expiresAt: string;
}

export const getInvitationPreview = async (token: string): Promise<InvitationPreview> => {
  const response = await fetch(`${API_BASE_URL}/invites/accept/${token}`);
  if (!response.ok) throw await parseError(response);
  return response.json();
};

export interface AcceptInvitationResponse {
  user: {
    id: string;
    email: string | null;
    fullName: string;
    role: Role;
    companyId: string;
  };
  customToken: string;
}

export const acceptInvitation = async (
  token: string,
  fullName: string,
  password: string,
): Promise<AcceptInvitationResponse> => {
  const response = await fetch(`${API_BASE_URL}/invites/accept/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fullName, password }),
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
};
